const PRODUCT_LISTING_QUERY_PREFIX = "plp";

export type ProductListingQueryState = {
  currentPage?: number;
  filters?: string;
  sort?: string;
};

type SearchParamsSource = Pick<URLSearchParams, "get">;

type ProductListingQueryKeys = {
  filters: string;
  page: string;
  sort: string;
};

export function getProductListingQueryKeys(
  listingId: string
): ProductListingQueryKeys {
  const prefix = `${PRODUCT_LISTING_QUERY_PREFIX}.${listingId}`;

  return {
    filters: `${prefix}.filters`,
    page: `${prefix}.page`,
    sort: `${prefix}.sort`,
  };
}

export function readProductListingQueryState(
  searchParams: SearchParamsSource,
  listingId: string,
  defaults: ProductListingQueryState
): ProductListingQueryState {
  const keys = getProductListingQueryKeys(listingId);

  return {
    currentPage: defaults.currentPage ?? 0,
    filters: searchParams.get(keys.filters) ?? defaults.filters,
    sort: searchParams.get(keys.sort) ?? defaults.sort,
  };
}

export function writeProductListingQueryState(
  searchParams: URLSearchParams,
  listingId: string,
  state: ProductListingQueryState,
  defaults: ProductListingQueryState
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  const keys = getProductListingQueryKeys(listingId);

  if (state.filters && state.filters !== defaults.filters) {
    next.set(keys.filters, state.filters);
  } else {
    next.delete(keys.filters);
  }

  if (state.sort && state.sort !== defaults.sort) {
    next.set(keys.sort, state.sort);
  } else {
    next.delete(keys.sort);
  }

  next.delete(keys.page);

  return next;
}

export type ProductListingApiQueryParts = {
  filters?: string;
  sort?: string;
};

/**
 * OCC facet links combine free text, sorting, and facets in one opaque query:
 * `<freeText>:<sort>:<facetKey>:<facetValue>...`.
 * Browser state keeps sorting separate, so remove that transport-only segment.
 */
export function splitProductListingApiQuery(
  apiQuery: string | undefined,
  sortCodes: readonly string[] = []
): ProductListingApiQueryParts {
  if (!apiQuery) {
    return {};
  }

  const segments = apiQuery.split(":");
  const embeddedSort = segments[1];
  const hasEmbeddedSort =
    segments.length > 1 &&
    (apiQuery.startsWith(":") ||
      (embeddedSort !== undefined && sortCodes.includes(embeddedSort)));

  if (!hasEmbeddedSort) {
    return { filters: apiQuery };
  }

  segments.splice(1, 1);

  return {
    filters: segments.join(":") || undefined,
    sort: embeddedSort || undefined,
  };
}

/** Reconstruct the OCC transport query immediately before the API request. */
export function buildProductListingApiQuery(
  filters: string | undefined,
  sort: string | undefined
): string | undefined {
  if (!(filters && sort)) {
    return filters || undefined;
  }

  const segments = filters.split(":");
  segments.splice(1, 0, sort);
  return segments.join(":");
}
